import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, IndianRupee, Boxes, Eye, ShoppingCart, Percent,
  Package, AlertTriangle, Star, RefreshCw
} from 'lucide-react';
import { analyticsAPI } from '../services/api';
import { LineChart, ColumnChart } from '../components/charts/Charts';

const RANGES = [
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
  { label: '1 year', value: 365 },
];

const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const formatNumber = (value) => Number(value || 0).toLocaleString('en-IN');

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
    <div className="flex items-center gap-2 text-gray-500 text-sm">
      <Icon size={16} className={accent} />
      {label}
    </div>
    <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
  </div>
);

const ProductAnalytics = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [metric, setMetric] = useState('revenue');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await analyticsAPI.getProductDetail(productId, days);
      setData(response);
    } catch (err) {
      console.error('Product analytics error:', err);
      setError(err.message || 'Failed to load product analytics');
    } finally {
      setLoading(false);
    }
  }, [productId, days]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate('/analytics')} className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-700">
          <ArrowLeft size={16} /> Back to Analytics
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertTriangle className="mx-auto text-red-500 mb-3" size={32} />
          <p className="text-red-700 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  const product = data?.product || {};
  const kpis = data?.kpis || {};

  const seriesByMetric = {
    revenue: { data: data?.revenue_series, color: '#6366f1', prefix: '₹' },
    units: { data: data?.units_series, color: '#22c55e', prefix: '' },
    views: { data: data?.views_series, color: '#0ea5e9', prefix: '' },
  };
  const activeSeries = seriesByMetric[metric];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <button onClick={() => navigate('/analytics')} className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-700 text-sm">
          <ArrowLeft size={16} /> Back to Analytics
        </button>
        <div className="flex items-center gap-2">
          <div className="flex bg-gray-100 rounded-lg p-1">
            {RANGES.map((r) => (
              <button
                key={r.value}
                onClick={() => setDays(r.value)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                  days === r.value ? 'bg-white text-indigo-600 shadow-sm font-medium' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <button onClick={loadData} className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
            <RefreshCw size={18} className="text-gray-600" />
          </button>
        </div>
      </div>

      {/* Product header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 flex items-center gap-5">
        <div className="w-20 h-24 rounded-lg overflow-hidden bg-gray-100 shrink-0">
          {product.image ? (
            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300"><Package size={28} /></div>
          )}
        </div>
        <div className="min-w-0">
          <h1 className="text-xl font-bold text-gray-900 truncate">{product.name}</h1>
          <p className="text-gray-500 text-sm mt-0.5">{product.collection}</p>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
            <span className="font-semibold text-gray-900">{formatCurrency(product.price)}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs ${product.total_stock === 0 ? 'bg-red-100 text-red-700' : product.total_stock < 10 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>
              {product.total_stock === 0 ? 'Out of stock' : `${product.total_stock} in stock`}
            </span>
            {product.rating ? (
              <span className="flex items-center gap-1 text-amber-500"><Star size={14} fill="currentColor" /> {product.rating} ({product.review_count || 0})</span>
            ) : null}
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Stat icon={IndianRupee} label="Revenue" value={formatCurrency(kpis.revenue)} accent="text-indigo-500" />
        <Stat icon={Boxes} label="Units Sold" value={formatNumber(kpis.units_sold)} accent="text-emerald-500" />
        <Stat icon={ShoppingCart} label="Orders" value={formatNumber(kpis.orders)} accent="text-sky-500" />
        <Stat icon={Eye} label="Views" value={formatNumber(kpis.views)} accent="text-purple-500" />
        <Stat icon={ShoppingCart} label="Add to Cart" value={formatNumber(kpis.add_to_cart)} accent="text-amber-500" />
        <Stat icon={Percent} label="Conversion" value={`${kpis.conversion_rate || 0}%`} accent="text-rose-500" />
      </div>

      {/* Trend */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Performance Trend</h3>
          <div className="flex bg-gray-100 rounded-lg p-1">
            {['revenue', 'units', 'views'].map((m) => (
              <button
                key={m}
                onClick={() => setMetric(m)}
                className={`px-3 py-1 text-sm rounded-md capitalize ${metric === m ? 'bg-white shadow-sm font-medium text-indigo-600' : 'text-gray-600'}`}
              >
                {m}
              </button>
            ))}
          </div>
        </div>
        <div className="p-5">
          <LineChart data={activeSeries.data || []} color={activeSeries.color} valuePrefix={activeSeries.prefix} />
        </div>
      </div>

      {/* Size breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-5 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Sales by Size</h3>
            <p className="text-sm text-gray-500 mt-0.5">Units sold per size</p>
          </div>
          <div className="p-5">
            <ColumnChart data={data?.size_sales || []} color="#6366f1" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-5 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Current Stock by Size</h3>
            <p className="text-sm text-gray-500 mt-0.5">Remaining inventory</p>
          </div>
          <div className="p-5">
            <ColumnChart data={data?.size_stock || []} color="#22c55e" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductAnalytics;
